import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { FunnelEntry, RequirementStatusCategory } from '../../models/dashboard.model';
import { projectColorVar } from '../../models/project-colors';

export interface ProjectFunnel {
  projectId: string;
  name: string;
  entries: FunnelEntry[];
  withoutStatusCount: number;
}

const CATEGORY_COLOR_VARS: Readonly<Record<RequirementStatusCategory, string>> = {
  OPEN: '--to-slate-dot',
  IN_PROGRESS: '--to-warn-dot',
  REVIEW: '--to-chart-running',
  APPROVED: '--to-chart-passed',
  DONE: '--to-info-dot',
};

const LEGACY_COLOR_VAR = '--to-slate-dot';

interface FunnelTile {
  status: string;
  count: number;
  label: string;
  tint: string;
  border: string;
  dot: string;
}

interface ProjectGroup {
  projectId: string;
  name: string;
  colorVar: string;
  requirementsLink: unknown[];
  tiles: FunnelTile[];
  withoutStatusCount: number;
}

@Component({
  selector: 'app-requirements-funnel',
  imports: [RouterLink, TranslatePipe, MatIcon, SectionTitleComponent],
  templateUrl: './requirements-funnel.component.html',
  styleUrl: './requirements-funnel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequirementsFunnelComponent {
  projects = input.required<ProjectFunnel[]>();
  colorOrder = input<readonly string[]>([]);

  protected readonly headingId = `requirements-funnel-heading-${crypto.randomUUID()}`;

  protected readonly groups = computed<ProjectGroup[]>(() =>
    this.projects().map((project, index) => ({
      projectId: project.projectId,
      name: project.name,
      colorVar: projectColorVar(project.projectId, this.colorOrder(), index),
      requirementsLink: ['/projects', project.projectId, 'requirements'],
      tiles: project.entries.map((entry) => this.toTile(entry)),
      withoutStatusCount: project.withoutStatusCount,
    })),
  );

  private toTile(entry: FunnelEntry): FunnelTile {
    const colorVar = entry.category ? CATEGORY_COLOR_VARS[entry.category] : LEGACY_COLOR_VAR;
    return {
      status: entry.status,
      count: entry.count,
      label: entry.status,
      tint: `color-mix(in srgb, var(${colorVar}) 3%, transparent)`,
      border: `color-mix(in srgb, var(${colorVar}) 19%, transparent)`,
      dot: `var(${colorVar})`,
    };
  }
}
